import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  EventCard,
  EventCardWrapper,
  EventContent,
} from "./EventSharedStyles";

const ToggleButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background: transparent;
  border: none;
  color: ${(props) => props.theme.blueish};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.24s ease, color 0.24s ease;

  &:hover {
    background-color: ${(props) => `${props.theme.lines}55`};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const SummaryText = styled.span`
  min-width: 0;
  color: ${(props) => props.theme.blueish};
  font-family: "SatoshiRegular";
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ToggleChevron = styled.span`
  flex-shrink: 0;
  color: ${(props) => props.theme.green};
  font-size: 14px;
  line-height: 1;
  transform: rotate(${(props) => (props.$isOpen ? "180deg" : "0deg")});
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
`;

const ContentShell = styled.div`
  min-height: 0;
  display: grid;
  grid-template-rows: ${(props) =>
    props.$isOpen ? "minmax(0, 1fr)" : "0fr"};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition:
    grid-template-rows 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.22s ease;
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
`;

const ContentInner = styled.div`
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollViewport = styled.div`
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 42px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0),
    ${(props) => props.theme.sidebar}
  );
  color: ${(props) => props.theme.green};
  font-size: 16px;
  line-height: 1;
`;

const SectionWrapper = styled(EventCardWrapper)`
  opacity: ${(props) => (props.$disabled ? 0.45 : 1)};
  transition: opacity 0.2s ease;
`;

export const useOverflowScrollIndicator = (enabled) => {
  const scrollRef = useRef(null);
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setShowIndicator(false);
      return undefined;
    }

    const element = scrollRef.current;
    if (!element) {
      return undefined;
    }

    const updateIndicator = () => {
      const hasOverflow = element.scrollHeight > element.clientHeight + 1;
      const isAtBottom =
        element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
      setShowIndicator(hasOverflow && !isAtBottom);
    };

    updateIndicator();
    element.addEventListener("scroll", updateIndicator);
    window.addEventListener("resize", updateIndicator);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateIndicator();
      });
      resizeObserver.observe(element);
    }

    return () => {
      element.removeEventListener("scroll", updateIndicator);
      window.removeEventListener("resize", updateIndicator);
      resizeObserver?.disconnect();
    };
  }, [enabled]);

  return {
    scrollRef,
    showIndicator,
  };
};

const defaultSummaryFormatter = (selectedLabels, emptySummary) => {
  if (!selectedLabels.length) {
    return emptySummary;
  }

  if (selectedLabels.length === 1) {
    return selectedLabels[0];
  }

  return `${selectedLabels[0]} + ${selectedLabels.length - 1}`;
};

const CollapsibleFilterSection = ({
  title,
  titleExtra = null,
  emptySummary,
  selectedLabels = [],
  children,
  defaultOpen = false,
  summaryFormatter = defaultSummaryFormatter,
  wrapChildrenInEventContent = true,
  isOpen: controlledIsOpen,
  onOpenChange,
  tourId,
  disabled = false,
  disabledTooltip,
}) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
  const isOpen =
    !disabled && (typeof controlledIsOpen === "boolean" ? controlledIsOpen : uncontrolledIsOpen);
  const usesInternalScroll = wrapChildrenInEventContent;
  const { scrollRef, showIndicator } = useOverflowScrollIndicator(
    isOpen && usesInternalScroll
  );
  const setIsOpen = (nextValue) => {
    if (disabled) {
      return;
    }

    if (typeof controlledIsOpen !== "boolean") {
      setUncontrolledIsOpen(nextValue);
    }
    onOpenChange?.(nextValue);
  };

  const summary = useMemo(
    () => summaryFormatter(selectedLabels, emptySummary),
    [emptySummary, selectedLabels, summaryFormatter]
  );

  return (
    <SectionWrapper
      data-tour={tourId}
      $disabled={disabled}
      title={disabled ? disabledTooltip : undefined}
    >
      <h3>
        {title}
        {titleExtra}
      </h3>
      <EventCard>
        <ToggleButton
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label={title}
          disabled={disabled}
          title={disabled ? disabledTooltip : undefined}
        >
          <SummaryText>{summary}</SummaryText>
          <ToggleChevron $isOpen={isOpen}>▾</ToggleChevron>
        </ToggleButton>
        <ContentShell $isOpen={isOpen} aria-hidden={!isOpen}>
          <ContentInner>
            {usesInternalScroll ? (
              <ScrollViewport>
                <ScrollArea ref={scrollRef}>
                  <EventContent>{children}</EventContent>
                </ScrollArea>
                {showIndicator ? <ScrollIndicator>↓</ScrollIndicator> : null}
              </ScrollViewport>
            ) : (
              children
            )}
          </ContentInner>
        </ContentShell>
      </EventCard>
    </SectionWrapper>
  );
};

export default CollapsibleFilterSection;
